import React, { useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, FormControlLabel, IconButton, InputAdornment, MenuItem, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { apiError } from '../format';
import { rulesApi } from './rulesApi';
import {
  BORDER, DARK, FIELD_SX, GHOST_BTN_SX, KIND_LABELS, MUTED, PERM_HINTS_RULES, PRIMARY, PRIMARY_BTN_SX, PRIMARY_SOFT, SELECT_MENU_PROPS, SOFT, TRACK,
  fmtDbDate, fmtRuleValue
} from './rulesConstants';
import { DIALOG_SLOT_PROPS, DialogHeader, EmptyState, HEAD_ROW_SX, ModeChip, Panel, ScopeCell, TABLE_SX } from './RuleBits';
import ExceptionEditDialog from './ExceptionEditDialog';
import { RuleRecord, RulesOverview } from './rulesTypes';
import { notify } from '../../../notifications';

interface Props {
  overview: RulesOverview;
  onChanged: () => void;
}

/** Вкладка «Исключения»: значения для отдельных аптек и направлений (перекрывают правило сети) */
const ExceptionsTab: React.FC<Props> = ({ overview, onChanged }) => {
  const { catalog, exceptions, senders } = overview;
  const [ruleFilter, setRuleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showExpired, setShowExpired] = useState(false);
  const [editing, setEditing] = useState<RuleRecord | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<RuleRecord | null>(null);
  const [busy, setBusy] = useState(false);

  const defOf = (code: string) => catalog.rules.find(r => r.code === code);
  const expiredCount = exceptions.filter(e => e.expired).length;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return exceptions.filter(e => {
      if (!showExpired && e.expired) return false;
      if (ruleFilter && e.rule_code !== ruleFilter) return false;
      if (q && !`${e.from_name || ''} ${e.to_name || ''} ${e.note || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [exceptions, ruleFilter, search, showExpired]);

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await rulesApi.remove(deleting.id);
      notify.success('Исключение удалено');
      setDeleting(null);
      onChanged();
    } catch (err: any) {
      notify.error(apiError(err, 'Не удалось удалить исключение'));
    } finally {
      setBusy(false);
    }
  };

  const noSenders = senders.length === 0;
  const hasAny = exceptions.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Поиск по аптеке или примечанию" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ width: 300, ...FIELD_SX }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 19, color: '#94a3b8' }} /></InputAdornment> }}
        />
        <TextField select size="small" value={ruleFilter} onChange={e => setRuleFilter(e.target.value)} sx={{ minWidth: 250, ...FIELD_SX }} SelectProps={{ displayEmpty: true, MenuProps: SELECT_MENU_PROPS }}>
          <MenuItem value="">Все правила</MenuItem>
          {catalog.rules.map(r => <MenuItem key={r.code} value={r.code}>{r.name}</MenuItem>)}
        </TextField>
        {expiredCount > 0 && (
          <FormControlLabel
            sx={{ ml: 0.5 }}
            control={
              <Switch
                size="small" checked={showExpired} onChange={e => setShowExpired(e.target.checked)}
                sx={{ '& .Mui-checked': { color: PRIMARY }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: PRIMARY } }}
              />
            }
            label={<Typography sx={{ fontSize: 13, color: MUTED }}>Показать истёкшие ({expiredCount})</Typography>}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={noSenders ? PERM_HINTS_RULES.noSenders : ''}>
          <span>
            <Button
              variant="contained" disableElevation startIcon={<AddRoundedIcon />} disabled={noSenders}
              onClick={() => setCreating(true)} sx={PRIMARY_BTN_SX}
            >
              Добавить исключение
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Panel>
        {rows.length === 0 ? (
          <EmptyState
            title={hasAny ? 'Ничего не найдено' : 'Исключений пока нет'}
            text={hasAny ? 'Измените поиск или фильтр по правилу.' : 'Везде действуют правила сети. Исключение позволяет задать другое значение для конкретной аптеки или направления.'}
          />
        ) : (
          <TableContainer>
            <Table size="small" sx={TABLE_SX}>
              <TableHead>
                <TableRow sx={HEAD_ROW_SX}>
                  <TableCell>Правило</TableCell>
                  <TableCell>Вид документа</TableCell>
                  <TableCell>Аптека / направление</TableCell>
                  <TableCell>Режим</TableCell>
                  <TableCell>Значение</TableCell>
                  <TableCell>Действует до</TableCell>
                  <TableCell>Примечание</TableCell>
                  <TableCell align="right" sx={{ width: 92 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(e => {
                  const def = defOf(e.rule_code);
                  return (
                    <TableRow key={e.id} sx={{ '& td': { py: 1.25 }, '& td, & .row-actions': { opacity: e.expired ? 0.6 : 1 }, '&:hover .row-actions': { opacity: 1 } }}>
                      <TableCell sx={{ color: DARK, fontWeight: 600 }}>{def?.name || e.rule_code}</TableCell>
                      <TableCell sx={{ color: MUTED }}>{KIND_LABELS[e.doc_kind]}</TableCell>
                      <TableCell><ScopeCell scopeType={e.scope_type} from={e.from_name} to={e.to_name} /></TableCell>
                      <TableCell><ModeChip mode={e.mode} label={e.mode === 'off' ? 'Не применять' : undefined} /></TableCell>
                      <TableCell sx={{ fontWeight: 600, color: DARK, whiteSpace: 'nowrap' }}>{e.mode === 'off' ? '-' : fmtRuleValue(def, e.value, e.period)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {e.valid_until ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {fmtDbDate(e.valid_until)}
                            {e.expired && (
                              <Box component="span" sx={{ px: 0.9, height: 20, display: 'inline-flex', alignItems: 'center', borderRadius: '6px', bgcolor: TRACK, color: MUTED, fontSize: 11.5, fontWeight: 600 }}>
                                Истекло
                              </Box>
                            )}
                          </Box>
                        ) : <Box component="span" sx={{ color: MUTED }}>Бессрочно</Box>}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, color: MUTED }}>
                        <Typography noWrap sx={{ fontSize: 13 }} title={e.note || ''}>{e.note || '-'}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box className="row-actions" sx={{ display: 'inline-flex', gap: 0.25 }}>
                          <Tooltip title="Изменить">
                            <IconButton size="small" onClick={() => setEditing(e)} aria-label="Изменить" sx={{ color: MUTED, '&:hover': { color: PRIMARY, bgcolor: PRIMARY_SOFT } }}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Удалить">
                            <IconButton size="small" onClick={() => setDeleting(e)} aria-label="Удалить" sx={{ color: MUTED, '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' } }}>
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Panel>

      <ExceptionEditDialog
        open={creating || Boolean(editing)} existing={editing} overview={overview}
        onClose={() => { setCreating(false); setEditing(null); }} onSaved={onChanged}
      />

      <Dialog open={Boolean(deleting)} onClose={busy ? undefined : () => setDeleting(null)} maxWidth="xs" fullWidth slotProps={DIALOG_SLOT_PROPS}>
        <DialogHeader
          title="Удалить исключение?"
          subtitle={deleting ? `${defOf(deleting.rule_code)?.name || deleting.rule_code} · ${KIND_LABELS[deleting.doc_kind]}` : undefined}
        />
        <Box sx={{ px: 3, py: 2.5 }}>
          {deleting && (
            <Box sx={{ mb: 1.5, px: 1.75, py: 1.25, borderRadius: '8px', bgcolor: SOFT, border: `1px solid ${BORDER}`, fontSize: 14 }}>
              <ScopeCell scopeType={deleting.scope_type} from={deleting.from_name} to={deleting.to_name} />
            </Box>
          )}
          <Typography sx={{ fontSize: 13.5, color: MUTED, lineHeight: 1.55 }}>
            После удаления для этой аптеки будет действовать правило сети. Удаление попадёт в историю изменений.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, py: 1.75, borderTop: `1px solid ${BORDER}`, bgcolor: SOFT }}>
          <Button onClick={() => setDeleting(null)} disabled={busy} sx={GHOST_BTN_SX}>Отмена</Button>
          <Button
            variant="contained" disableElevation onClick={remove} disabled={busy}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Удалить
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ExceptionsTab;
